import React from 'react';

import { useGetStream } from '@vlabs/api/artemy/client';
import { StreamTargetsParameterItem } from '@vlabs/api/artemy/model';
import type { StreamId } from '@vlabs/api/artemy/model';
import {
  buildLocationString,
  toStreamLocationRecord,
} from '@vlabs/monokit/entities/sources';
import { GeoLocation, GeoLocationMarker, useModal } from '@vlabs/uikit';
import { useDynamicCoordinates } from '@vlabs/uikit/components/geo-location/useDynamicCoordinates';

import { useSourceLivePageTranslation } from '../i18n';
import st from './StreamMapModal.module.sass';

interface StreamMapModalProps {
  streamId: StreamId;
}

function formatCoordinate(value: number | undefined): string | undefined {
  if (value === undefined) return undefined;
  return value.toFixed(4);
}

// Миниатюра карты в углу видео; клик открывает карту с пином источника
// и информацией о геопозиции (макет «Отображение источника на карте»).
// Самодостаточна: сама запрашивает геопозицию потока (targets=['location']);
// без геокоординат миниатюра не отображается.
function StreamMapModal({ streamId }: StreamMapModalProps): React.JSX.Element | null {
  const { t } = useSourceLivePageTranslation();
  const modal = useModal({ size: 'large', scrollable: true });

  const { data: stream } = useGetStream(streamId, {
    targets: [StreamTargetsParameterItem.location],
  });

  const record = toStreamLocationRecord(stream?.location);
  const { latitude, longitude } = record?.geo_position ?? {};
  const hasCoordinates = latitude !== undefined && longitude !== undefined;

  const position = useDynamicCoordinates({
    latitude: latitude ?? '',
    longitude: longitude ?? '',
  });

  if (!hasCoordinates) return null;

  const address = buildLocationString(record);

  const marker = <GeoLocationMarker anchor={position} />;

  const thumbnailMap = (
    <GeoLocation
      center={position}
      mapProps={{ mouseEvents: false, touchEvents: false }}
      toggleFullScreenMode={false}
    >
      {marker}
    </GeoLocation>
  );

  let addressRow: React.JSX.Element | null = null;
  if (address) {
    addressRow = <p className={st.Row}>{address}</p>;
  }

  const infoBlock = (
    <div className={st.Info}>
      <p className={st.InfoTitle}>{t('location_title')}</p>
      <div className={st.InfoRows}>
        {addressRow}
        <p className={st.Row}>
          <span className={st.Label}>{t('longitude')}</span>
          <span>{formatCoordinate(longitude)}</span>
        </p>
        <p className={st.Row}>
          <span className={st.Label}>{t('latitude')}</span>
          <span>{formatCoordinate(latitude)}</span>
        </p>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      {modal.wrap(
        <div className={st.Modal}>
          <GeoLocation center={position}>
            {marker}
          </GeoLocation>
          {infoBlock}
        </div>,
      )}

      <button
        aria-label={t('map')}
        className={st.Thumbnail}
        data-testid="stream-map-thumbnail"
        onClick={modal.open}
        type="button"
      >
        {thumbnailMap}
      </button>
    </React.Fragment>
  );
}

export { StreamMapModal };
